import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import ReusableTable from "../components/ReusableTable.js";
import DateFilter from "../components/Forms/DateFilter.js";
import { BasicSelect } from "../components/Forms/BasicSelect.js";
import {
  FiAlertTriangle,
  FiDownload,
  FiFileText,
  FiFilter,
  FiInfo,
  FiPrinter,
  FiSearch,
} from "react-icons/fi";
import { StatusBadge } from "../components/StatusBadge.js";
import { getShifts } from "../services/shifts.js";
import { getServiceUsers, getStaffMembers } from "../services/users.js";
import { getProviderName } from "../utils/providerName.js";
import { useTheme } from "../context/ThemeContext.js";
import { formatUKTime, toUKWallTime } from "../utils/functions.js";
import {
  currentUserId,
  loadReportState,
  saveReportState,
} from "../utils/careNotesReportState.js";
import {
  EMPTY_FILTERS,
  DEFAULT_SORT,
  LATEST_NOTE_NOTICE,
  SHIFT_FETCH_LIMIT,
  STATUS_OPTIONS,
  VISIT_PERIOD_OPTIONS,
  buildShiftQuery,
  canViewCareNotesReports,
  clampPageIndex,
  describeReport,
  filterCareNotes,
  formatNoteDateTime,
  formatVisitTime,
  getOutputRows,
  getPageSummary,
  getSelectedShownRows,
  inclusiveDateTo,
  nextSort,
  reportFileName,
  searchCareNotes,
  shiftsToCareNotes,
  sortCareNotes,
  summariseNote,
  truncatedNotice,
  validateReportFilters,
} from "../utils/careNotesReport.js";

const SortHeader = ({ label, sortKey, sort, onSort }) => (
  <button
    type="button"
    className="cnr__sort"
    onClick={() => onSort(sortKey)}
    title={`Sort by ${label}`}
  >
    {label}
    <span className="sort-indicator">
      {sort.key === sortKey ? (sort.direction === "desc" ? " ↓" : " ↑") : " ⇅"}
    </span>
  </button>
);

const CareNotesReport = ({ serviceUserOpt, staffOpt }) => {
  const { theme } = useTheme();
  const dateFilterRef = useRef();
  // Coming back from a shift (or any page) restores the last report
  const [userId] = useState(currentUserId);
  const [restored] = useState(() => loadReportState(userId));
  const [filters, setFilters] = useState(restored?.filters ?? EMPTY_FILTERS);
  // The criteria and results of the last "Generate Report"
  const [report, setReport] = useState(restored?.report ?? null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [search, setSearch] = useState(restored?.search ?? "");
  const [sort, setSort] = useState(restored?.sort ?? DEFAULT_SORT);
  const [pageIndex, setPageIndex] = useState(restored?.pageIndex ?? 0);
  const [selectedIds, setSelectedIds] = useState(
    () => new Set(restored?.selectedIds ?? [])
  );
  // Only the latest Generate/Clear counts; older responses are ignored
  const requestIdRef = useRef(0);
  // Window scroll plus the table's own scroll box (it scrolls both ways)
  const scrollRef = useRef(
    restored?.scroll ?? { windowY: 0, tableTop: 0, tableLeft: 0 }
  );
  const tableWrapRef = useRef(null);

  useEffect(() => {
    saveReportState(userId, {
      filters,
      report,
      search,
      sort,
      pageIndex,
      selectedIds: [...selectedIds],
      scroll: scrollRef.current,
    });
  }, [userId, filters, report, search, sort, pageIndex, selectedIds]);

  // Remember the scroll position and put it back on return. The layout
  // scrolls to the top on every route change after this effect runs, so
  // restore on the next frame.
  useEffect(() => {
    const tableBox = tableWrapRef.current?.querySelector(".table");
    const onWindowScroll = () => {
      scrollRef.current = { ...scrollRef.current, windowY: window.scrollY };
    };
    const onTableScroll = () => {
      scrollRef.current = {
        ...scrollRef.current,
        tableTop: tableBox.scrollTop,
        tableLeft: tableBox.scrollLeft,
      };
    };
    window.addEventListener("scroll", onWindowScroll, { passive: true });
    tableBox?.addEventListener("scroll", onTableScroll, { passive: true });
    const saved = restored?.scroll;
    const frame = saved
      ? window.requestAnimationFrame(() => {
          if (tableBox) {
            tableBox.scrollTop = saved.tableTop;
            tableBox.scrollLeft = saved.tableLeft;
          }
          window.scrollTo(0, saved.windowY);
        })
      : null;
    return () => {
      window.removeEventListener("scroll", onWindowScroll);
      tableBox?.removeEventListener("scroll", onTableScroll);
      if (frame) window.cancelAnimationFrame(frame);
      requestIdRef.current += 1;
      const state = loadReportState(userId);
      if (state) saveReportState(userId, { ...state, scroll: scrollRef.current });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilter = (name, value) =>
    setFilters((prev) => ({ ...prev, [name]: value }));

  const sortedRows = useMemo(
    () => sortCareNotes(report?.rows ?? [], sort),
    [report, sort]
  );
  const shownRows = useMemo(
    () => searchCareNotes(sortedRows, search),
    [sortedRows, search]
  );
  const currentPage = clampPageIndex(pageIndex, shownRows.length);
  const selectedShownRows = getSelectedShownRows(shownRows, selectedIds);

  const generateReport = async () => {
    const error = validateReportFilters(filters);
    if (error) {
      toast.error(error);
      return;
    }
    const requestId = ++requestIdRef.current;
    const criteria = filters;
    setIsLoading(true);
    try {
      const { data } = await getShifts(buildShiftQuery(criteria));
      if (requestId !== requestIdRef.current) return;
      const shifts = Array.isArray(data) ? data : [];
      setReport({
        filters: criteria,
        generatedAt: new Date(),
        truncated: shifts.length >= SHIFT_FETCH_LIMIT,
        rows: filterCareNotes(shiftsToCareNotes(shifts), criteria),
      });
      setSelectedIds(new Set());
      setSearch("");
      setSort(DEFAULT_SORT);
      setPageIndex(0);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error(err);
      toast.error("Failed to generate the care notes report");
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  };

  const clearFilters = () => {
    requestIdRef.current += 1;
    setIsLoading(false);
    setFilters(EMPTY_FILTERS);
    dateFilterRef.current?.handleClearFilter();
    setReport(null);
    setSelectedIds(new Set());
    setSearch("");
    setPageIndex(0);
  };

  const onSearchChange = (value) => {
    setSearch(value);
    setPageIndex(0);
  };

  const toggleSelected = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const allShownSelected =
    shownRows.length > 0 && shownRows.every((row) => selectedIds.has(row.id));

  const toggleAllShown = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      shownRows.forEach((row) =>
        allShownSelected ? next.delete(row.id) : next.add(row.id)
      );
      return next;
    });

  const output = async (kind) => {
    const rows = getOutputRows(shownRows, selectedIds);
    const meta = describeReport({
      ...report,
      search,
      selectedCount: selectedShownRows.length,
      providerName: getProviderName(),
    });
    setIsExporting(true);
    try {
      const exporter = await import("../utils/careNotesExport.js");
      if (kind === "print") {
        exporter.printCareNotes(meta, rows);
      } else if (kind === "pdf") {
        await exporter.exportCareNotesPdf(
          meta,
          rows,
          reportFileName(report.generatedAt, "pdf")
        );
      } else {
        await exporter.exportCareNotesExcel(
          meta,
          rows,
          reportFileName(report.generatedAt, "xlsx")
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to export the care notes report");
    } finally {
      setIsExporting(false);
    }
  };

  const onSort = (key) => {
    setSort((current) => nextSort(current, key));
    setPageIndex(0);
  };

  const columns = useMemo(() => {
    const sortable = (label, sortKey) => () =>
      <SortHeader label={label} sortKey={sortKey} sort={sort} onSort={onSort} />;
    return [
      {
        id: "select",
        header: () => (
          <input
            type="checkbox"
            className="cnr__checkbox"
            aria-label={`Select all ${shownRows.length} results`}
            title={`Select all ${shownRows.length} results (every page)`}
            checked={allShownSelected}
            onChange={toggleAllShown}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="cnr__checkbox"
            aria-label={`Select care note ${row.original.rotaRef}`}
            checked={selectedIds.has(row.original.id)}
            onChange={() => toggleSelected(row.original.id)}
          />
        ),
      },
      {
        id: "dateTime",
        header: sortable("Date & Time", "dateTime"),
        cell: ({ row }) => formatNoteDateTime(row.original),
      },
      {
        id: "rotaRef",
        header: sortable("Rota Ref", "rotaRef"),
        cell: ({ row }) => (
          <Link className="cnr__link" to={`/shift/${row.original.shiftId}`}>
            {row.original.rotaRef}
          </Link>
        ),
      },
      {
        id: "serviceUser",
        header: sortable("Service User", "serviceUser"),
        cell: ({ row }) => row.original.serviceUser,
      },
      {
        id: "staffMember",
        header: sortable("Staff Member", "staffMember"),
        cell: ({ row }) => row.original.staffMember,
      },
      {
        id: "careType",
        header: sortable("Care Type", "careType"),
        cell: ({ row }) => row.original.careType,
      },
      {
        id: "visitTime",
        header: "Shift / Visit Time",
        cell: ({ row }) => formatVisitTime(row.original),
      },
      {
        id: "status",
        header: sortable("Status", "status"),
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "note",
        header: "Note Summary",
        cell: ({ row }) => (
          <span className="cnr__note" title={row.original.note}>
            {summariseNote(row.original.note)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <Link className="cnr__link" to={`/shift/${row.original.shiftId}`}>
            View shift
          </Link>
        ),
      },
    ].map((column) => ({ ...column, enableSorting: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, selectedIds, allShownSelected, shownRows]);

  const canOutput =
    Boolean(report) && !isLoading && !isExporting && shownRows.length > 0;
  // Restored or older results no longer match what the filters now say
  const filtersChanged =
    Boolean(report) && JSON.stringify(filters) !== JSON.stringify(report.filters);

  const OUTPUTS = [
    { kind: "print", label: "Print", Icon: FiPrinter },
    { kind: "pdf", label: "Export PDF", Icon: FiFileText },
    { kind: "excel", label: "Export Excel", Icon: FiDownload },
  ];

  return (
    <div className="cnr__panel" data-theme={theme}>
      <section className="card cnr__filters-card" aria-label="Report filters">
        <header className="cnr__section-header">
          <h3 className="cnr__section-title">
            <FiFilter aria-hidden="true" /> Report filters
          </h3>
          <p className="cnr__muted">
            Every filter is optional. Pick one service user or staff member for a
            care review, safeguarding or documentation report.
          </p>
        </header>

        <div className="cnr__filters">
          <div className="cnr__field">
            <label className="cnr__label" htmlFor="cnr-service-user">
              Service user
            </label>
            <BasicSelect
              inputId="cnr-service-user"
              placeholder="All service users"
              options={serviceUserOpt}
              name="serviceUsers"
              value={filters.serviceUsers}
              onChange={(e) => setFilter("serviceUsers", e || [])}
              isSearchable={true}
              isMulti={true}
              classNamePrefix="cnr-select"
              height="40px"
            />
          </div>
          <div className="cnr__field">
            <label className="cnr__label" htmlFor="cnr-staff">
              Staff member
            </label>
            <BasicSelect
              inputId="cnr-staff"
              placeholder="All staff members"
              options={staffOpt}
              name="staff"
              value={filters.staff}
              onChange={(e) => setFilter("staff", e || [])}
              isSearchable={true}
              isMulti={true}
              classNamePrefix="cnr-select"
              height="40px"
            />
          </div>
          <div className="cnr__field cnr__field--wide">
            <span className="cnr__label" id="cnr-visit-dates">
              Visit dates
            </span>
            <span id="cnr-visit-from" hidden>
              from
            </span>
            <span id="cnr-visit-to" hidden>
              to
            </span>
            <DateFilter
              startLabelledBy="cnr-visit-dates cnr-visit-from"
              endLabelledBy="cnr-visit-dates cnr-visit-to"
              ref={dateFilterRef}
              dateFormat="dd/MM/yyyy"
              startPlaceholder="From"
              endPlaceholder="To"
              height="40px"
              initialStartDate={toUKWallTime(restored?.filters?.dateFrom) ?? ""}
              initialEndDate={toUKWallTime(restored?.filters?.dateTo) ?? ""}
              onFilterChange={(range) =>
                setFilters((prev) => ({
                  ...prev,
                  dateFrom: range.startDate || "",
                  dateTo: inclusiveDateTo(range.endDate) || "",
                }))
              }
            />
          </div>
          <div className="cnr__field">
            <label className="cnr__label" htmlFor="cnr-visit-time">
              Shift / visit time
            </label>
            <BasicSelect
              inputId="cnr-visit-time"
              placeholder="All times"
              options={VISIT_PERIOD_OPTIONS}
              name="visitPeriod"
              value={filters.visitPeriod}
              onChange={(e) => setFilter("visitPeriod", e)}
              isClearable={true}
              height="40px"
            />
          </div>
          <div className="cnr__field">
            <label className="cnr__label" htmlFor="cnr-status">
              Status
            </label>
            <BasicSelect
              inputId="cnr-status"
              placeholder="All statuses"
              options={STATUS_OPTIONS}
              name="statuses"
              value={filters.statuses}
              onChange={(e) => setFilter("statuses", e || [])}
              isMulti={true}
              classNamePrefix="cnr-select"
              height="40px"
            />
          </div>
          <label className="cnr__field cnr__field--full">
            <span className="cnr__label">Contains text</span>
            <span className="cnr__search">
              <FiSearch className="cnr__search-icon" aria-hidden="true" />
              <input
                type="text"
                className="cnr__input"
                placeholder="Search within care notes, e.g. medication, lunch, fall"
                value={filters.keyword}
                onChange={(e) => setFilter("keyword", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !isLoading && generateReport()}
              />
            </span>
          </label>
        </div>

        <footer className="cnr__filter-actions">
          <button type="button" className="btn btn__link" onClick={clearFilters}>
            Clear filters
          </button>
          <button
            type="button"
            className="btn btn__primary cnr__btn cnr__btn--generate"
            disabled={isLoading}
            onClick={generateReport}
          >
            {isLoading ? "Generating…" : "Generate Report"}
          </button>
        </footer>
      </section>

      {report?.truncated && (
        <div className="cnr__callout cnr__callout--warning" role="alert">
          <FiAlertTriangle aria-hidden="true" />
          <span>{truncatedNotice()}</span>
        </div>
      )}

      <div className="cnr__results-bar">
        <div className="cnr__results-heading">
          <h3 className="cnr__section-title">
            Care notes
            {report && <span className="cnr__count">{shownRows.length}</span>}
          </h3>
          {report && (
            <span className="cnr__meta">
              Generated {formatUKTime(report.generatedAt, "dd MMM yyyy, HH:mm")}
              {filtersChanged && (
                <span className="cnr__stale">
                  {" "}
                  · Filters changed — select Generate Report to update
                </span>
              )}
            </span>
          )}
        </div>
        <div className="cnr__toolbar">
          <label className="cnr__search cnr__search--results">
            <FiSearch className="cnr__search-icon" aria-hidden="true" />
            <input
              type="text"
              className="cnr__input"
              placeholder="Search results..."
              aria-label="Search results"
              value={search}
              disabled={!report}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </label>
          {selectedShownRows.length > 0 && (
            <span className="cnr__selection">
              {selectedShownRows.length} selected
              <button
                type="button"
                className="btn btn__link"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear
              </button>
            </span>
          )}
          {OUTPUTS.map(({ kind, label, Icon }) => (
            <button
              key={kind}
              type="button"
              className="btn btn--outline cnr__btn"
              disabled={!canOutput}
              title={
                selectedShownRows.length
                  ? `Uses the ${selectedShownRows.length} selected care note(s)`
                  : "Uses every care note in the results"
              }
              onClick={() => output(kind)}
            >
              <Icon aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div ref={tableWrapRef}>
      <ReusableTable
        data={shownRows}
        columns={columns}
        isLoading={isLoading}
        pageIndex={currentPage}
        onPageChange={setPageIndex}
        emptyText={
          report
            ? "No care notes match the report filters"
            : "Choose your filters and select Generate Report"
        }
      />
      </div>

      {report && (
        <p className="cnr__summary">{getPageSummary(shownRows.length, currentPage)}</p>
      )}
    </div>
  );
};

const userGroups = () => {
  const userString = localStorage.getItem("user");
  return userString ? JSON.parse(userString)?.userGroup || [] : [];
};

export default function CareNotesReports() {
  const [serviceUserOpt, setServiceUserOpt] = useState([]);
  const [staffOpt, setStaffOpt] = useState([]);
  const allowed = canViewCareNotesReports(userGroups());

  useEffect(() => {
    if (!allowed) return undefined;
    let ignore = false;
    getServiceUsers()
      .then(({ data }) =>
        !ignore &&
        setServiceUserOpt(
          (data || []).map((user) => ({
            label: `${user.firstName} ${user.lastName}`,
            value: user.id,
          }))
        )
      )
      .catch(console.error);
    getStaffMembers()
      .then(({ data }) =>
        !ignore &&
        setStaffOpt(
          (data || []).map((user) => ({
            label: `${user.firstName} ${user.lastName}`,
            value: user.staffId,
          }))
        )
      )
      .catch(console.error);
    return () => {
      ignore = true;
    };
  }, [allowed]);

  if (!allowed) {
    return <p>You do not have permission to view care notes reports.</p>;
  }

  return (
    <div className="cnr">
      <p className="cnr__intro">Filter, review and print care notes</p>
      <div className="cnr__callout">
        <FiInfo aria-hidden="true" />
        <span>{LATEST_NOTE_NOTICE}</span>
      </div>
      <CareNotesReport serviceUserOpt={serviceUserOpt} staffOpt={staffOpt} />
    </div>
  );
}
